import { useEffect, useState, useCallback } from "react";
import { Link } from "wouter";
import { Check, ArrowRight, ArrowDown } from "lucide-react";
import { MarketingLayout } from "../components/MarketingLayout";
import { Reveal } from "../components/Reveal";
import { useSEO } from "../lib/useSEO";
import {
  FEATURE_CATEGORIES,
  FEATURE_NAV,
  INTEGRATION_HUB,
  PROCUREMENT_FLOW,
  APPROVAL_FLOW,
  INDIA_COMPLIANCE,
  type FeatureModule,
} from "../lib/content";
import {
  HRMSPreview,
  PayrollPreview,
  FinancePreview,
  ProcurementPreview,
  VendorPortalPreview,
  SelfServicePreview,
  AnalyticsPreview,
  RBACPreview,
  ApprovalEnginePreview,
  AuditLogsPreview,
  ReportsPreview,
  LearningTravelPreview,
} from "../components/FeaturePreviews";

const MODULE_PREVIEWS: Record<string, React.ComponentType> = {
  HRMS: HRMSPreview,
  Payroll: PayrollPreview,
  Finance: FinancePreview,
  Procurement: ProcurementPreview,
  "Vendor Portal": VendorPortalPreview,
  "Employee Self-Service": SelfServicePreview,
  Analytics: AnalyticsPreview,
  RBAC: RBACPreview,
  "Approval Engine": ApprovalEnginePreview,
  "Audit Logs": AuditLogsPreview,
  Reports: ReportsPreview,
  "Learning & Travel": LearningTravelPreview,
};

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  // Update hash without jumping
  history.replaceState(null, "", `#${id}`);
}

function ModulePreview({ module }: { module: FeatureModule }) {
  const Preview = MODULE_PREVIEWS[module.id];
  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4 shadow-md ring-1 ring-black/[0.03]">
      <div
        role="img"
        aria-label={`Sample ${module.name} interface preview`}
        className="w-full min-w-0 overflow-hidden rounded-xl"
      >
        {Preview ? (
          <Preview />
        ) : (
          <div className="flex aspect-[16/9] items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white">
            <span className="text-xs font-medium uppercase tracking-wider text-slate-300">
              {module.name} preview
            </span>
          </div>
        )}
      </div>
      <ul className="mt-3 grid grid-cols-2 gap-2" aria-label={`${module.name} capabilities`}>
        {module.capabilities.map((c) => (
          <li
            key={c}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs font-medium text-slate-600 truncate"
            title={c}
          >
            {c}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ModuleCopy({ module }: { module: FeatureModule }) {
  return (
    <div className="w-full min-w-0 max-w-full">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white">
        <module.icon aria-hidden="true" className="h-6 w-6" />
      </div>
      <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-blue-600">
        {module.name}
      </p>
      <h3 className="mt-2 text-2xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-3xl break-words">
        {module.headline}
      </h3>
      <p className="mt-3 text-base leading-relaxed text-slate-600 sm:text-lg break-words">
        {module.description}
      </p>
      <ul className="mt-6 space-y-2.5">
        {module.capabilities.map((c) => (
          <li key={c} className="flex items-start gap-2.5 text-sm text-slate-700">
            <Check aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
            {c}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FlowStrip({
  steps,
  dark = false,
}: {
  steps: { label: string }[];
  dark?: boolean;
}) {
  return (
    <ol className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-3">
      {steps.map((step, i) => (
        <li key={step.label} className="flex items-center gap-1.5 sm:gap-3">
          <span
            className={`rounded-xl px-2.5 py-1.5 text-xs font-semibold sm:px-3.5 sm:py-2 sm:text-sm ${
              dark
                ? "border border-white/15 bg-white/10 text-white"
                : "border border-slate-200 bg-white text-[#0a2d4a] shadow-sm"
            }`}
          >
            {step.label}
          </span>
          {i < steps.length - 1 && (
            <ArrowRight
              aria-hidden="true"
              className={`h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4 ${dark ? "text-blue-300" : "text-blue-500"}`}
            />
          )}
        </li>
      ))}
    </ol>
  );
}

export default function Features() {
  useSEO({
    title: "ERP Modules — HR, Payroll, Finance, Procurement & More | Alea ERP",
    description:
      "Explore Alea ERP modules that connect HR, payroll, finance, procurement, approvals and reporting in one integrated business platform — built for Indian and growing enterprises.",
    path: "/features",
  });

  const [activeId, setActiveId] = useState(FEATURE_NAV[0]?.id ?? "");

  const onIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    const visible = entries
      .filter((e) => e.isIntersecting)
      .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
    if (visible[0]?.target.id) {
      setActiveId(visible[0].target.id);
    }
  }, []);

  useEffect(() => {
    const sections = FEATURE_NAV.map((n) => document.getElementById(n.id)).filter(
      Boolean,
    ) as HTMLElement[];
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(onIntersect, {
      rootMargin: "-20% 0px -55% 0px",
      threshold: [0, 0.25, 0.5],
    });
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [onIntersect]);

  // Global flip counter across category modules for alternating layout
  let flipIndex = 0;

  return (
    <MarketingLayout>
      {/* ───────────────── Hero ───────────────── */}
      <section className="relative overflow-hidden bg-[#0a2d4a] pt-32 pb-16 lg:pt-40 lg:pb-20">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 left-1/2 h-96 w-full max-w-[700px] -translate-x-1/2 rounded-full bg-blue-500/20 blur-[120px]"
        />
        <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-300">
              Product depth
            </span>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
              Everything Your Business Needs.
              <br />
              <span className="bg-gradient-to-r from-blue-300 to-blue-500 bg-clip-text text-transparent">
                Connected in One ERP.
              </span>
            </h1>
            <p className="mt-6 text-base leading-relaxed text-slate-300 sm:text-lg">
              Explore the modules that connect people, payroll, finance, procurement,
              operations, reporting and approvals in one platform — not a pile of
              disconnected tools.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ───────────────── Sticky module nav ───────────────── */}
      <nav
        aria-label="Feature categories"
        className="sticky top-16 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-md"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <ul className="flex gap-1.5 overflow-x-auto py-2.5 sm:py-3 snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {FEATURE_NAV.map((item) => {
              const active = activeId === item.id;
              return (
                <li key={item.id} className="shrink-0 snap-start">
                  <button
                    type="button"
                    onClick={() => scrollToId(item.id)}
                    aria-current={active ? "true" : undefined}
                    className={`rounded-lg px-3 py-1.5 text-xs sm:px-3.5 sm:py-2 sm:text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                      active
                        ? "bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                        : "text-slate-600 hover:bg-slate-50 hover:text-[#0a2d4a]"
                    }`}
                  >
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {/* ───────────────── How everything connects ───────────────── */}
      <section className="bg-white py-16 sm:py-20" aria-labelledby="integration-heading">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
              Integration
            </span>
            <h2 id="integration-heading" className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl">
              One ERP. Every function connected.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              The value isn&apos;t just having multiple modules. It&apos;s having business
              data and workflows move across them from one platform.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mx-auto mt-12 max-w-3xl">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-6 sm:p-8">
              <div className="flex flex-wrap justify-center gap-2">
                {INTEGRATION_HUB.map((mod) => (
                  <span
                    key={mod}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-[#0a2d4a] shadow-sm"
                  >
                    {mod}
                  </span>
                ))}
              </div>
              <div className="my-4 flex flex-col items-center gap-1 text-slate-400">
                <ArrowDown aria-hidden="true" className="h-5 w-5" />
                <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                  Shared business data
                </span>
                <ArrowDown aria-hidden="true" className="h-5 w-5" />
              </div>
              <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:gap-3">
                <span className="rounded-xl bg-[#0a2d4a] px-4 py-2 text-sm font-semibold text-white">
                  Reports &amp; Analytics
                </span>
                <ArrowRight aria-hidden="true" className="hidden h-4 w-4 text-blue-500 sm:block" />
                <ArrowDown aria-hidden="true" className="h-4 w-4 text-blue-500 sm:hidden" />
                <span className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-800">
                  Approvals &amp; Audit
                </span>
              </div>
              <p className="mt-6 text-center text-sm text-slate-500">
                The advantage is integration — not merely the number of modules.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────────── Category sections ───────────────── */}
      {FEATURE_CATEGORIES.map((category) => {
        const isWorkflows = category.id === "workflows";
        const isFinanceOps = category.id === "finance-ops";

        return (
          <section
            key={category.id}
            id={category.id}
            className={`scroll-mt-32 py-16 sm:py-20 ${
              category.id === "hr-people" || category.id === "workflows"
                ? "bg-slate-50"
                : "bg-white"
            }`}
            aria-labelledby={`${category.id}-heading`}
          >
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
              <Reveal className="mx-auto max-w-2xl text-center">
                <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
                  {category.navLabel}
                </span>
                <h2
                  id={`${category.id}-heading`}
                  className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl"
                >
                  {category.title}
                </h2>
                <p className="mt-4 text-lg text-slate-600">{category.intro}</p>
              </Reveal>

              {/* Approval flow callout inside Workflows */}
              {isWorkflows && (
                <Reveal className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                  <h3 className="text-center text-base font-bold text-[#0a2d4a]">
                    Move requests through configurable approval workflows
                  </h3>
                  <p className="mx-auto mt-2 max-w-xl text-center text-sm text-slate-500">
                    Without manual follow-ups — every hand-off tracked, every decision recorded.
                  </p>
                  <div className="mt-6">
                    <FlowStrip steps={APPROVAL_FLOW} />
                  </div>
                </Reveal>
              )}

              {/* Procurement flow callout inside Finance & Ops */}
              {isFinanceOps && (
                <Reveal className="mt-10 rounded-2xl border border-slate-200 bg-slate-50/80 p-6 sm:p-8">
                  <h3 className="text-center text-base font-bold text-[#0a2d4a]">
                    Procurement connected to approvals, vendors and finance
                  </h3>
                  <p className="mx-auto mt-2 max-w-xl text-center text-sm text-slate-500">
                    A single operational flow — from requisition to purchase order to
                    vendor and finance — inside one ERP.
                  </p>
                  <div className="mt-6">
                    <FlowStrip steps={PROCUREMENT_FLOW} />
                  </div>
                </Reveal>
              )}

              {/* Paired layout (Analytics + Reports) */}
              {category.layout === "paired" ? (
                <div className="mt-14 grid gap-10 lg:grid-cols-2 min-w-0 w-full">
                  {category.modules.map((module, i) => (
                    <Reveal key={module.id} delay={i * 0.08} className="min-w-0 w-full">
                      <div className="flex h-full flex-col gap-6 min-w-0 w-full">
                        <ModuleCopy module={module} />
                        <ModulePreview module={module} />
                      </div>
                    </Reveal>
                  ))}
                </div>
              ) : (
                <div className="mt-14 space-y-20 min-w-0 w-full">
                  {category.modules.map((module) => {
                    const flip = flipIndex % 2 === 1;
                    flipIndex += 1;

                    // RBAC + Audit: present as enterprise control pair after Approval
                    const isControlPair =
                      isWorkflows && (module.id === "RBAC" || module.id === "Audit Logs");

                    if (isWorkflows && module.id === "RBAC") {
                      const audit = category.modules.find((m) => m.id === "Audit Logs");
                      if (!audit) return null;
                      return (
                        <Reveal key="enterprise-control" className="min-w-0 w-full">
                          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-8 min-w-0 w-full">
                            <h3 className="text-xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-2xl">
                              Enterprise Control &amp; Security
                            </h3>
                            <p className="mt-2 max-w-2xl text-sm text-slate-600">
                              Role-based access and audit logs work together so sensitive
                              data stays controlled and important actions stay traceable —
                              without inventing certifications or SLA claims.
                            </p>
                            <div className="mt-8 grid gap-10 lg:grid-cols-2 min-w-0 w-full">
                              {[module, audit].map((m) => (
                                <div key={m.id} className="grid items-start gap-6 min-w-0 w-full">
                                  <ModuleCopy module={m} />
                                  <ModulePreview module={m} />
                                </div>
                              ))}
                            </div>
                          </div>
                        </Reveal>
                      );
                    }
                    if (isControlPair && module.id === "Audit Logs") return null;

                    return (
                      <Reveal key={module.id} className="min-w-0 w-full">
                        <div
                          className={`grid items-center gap-10 lg:grid-cols-2 min-w-0 w-full ${
                            flip ? "lg:[&>*:first-child]:order-2" : ""
                          }`}
                        >
                          <ModuleCopy module={module} />
                          <ModulePreview module={module} />
                        </div>
                      </Reveal>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* ───────────────── India-ready ───────────────── */}
      <section className="bg-white py-16 sm:py-20" aria-labelledby="india-heading">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:items-center">
            <Reveal>
              <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
                Local compliance
              </span>
              <h2
                id="india-heading"
                className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl"
              >
                Built for Indian businesses
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">
                Payroll and finance in Alea ERP include Indian statutory capabilities —
                so compliance is part of the product, not a bolted-on spreadsheet.
              </p>
            </Reveal>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {INDIA_COMPLIANCE.map((item, i) => (
                <Reveal key={item.label} delay={i * 0.05}>
                  <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
                    <item.icon aria-hidden="true" className="h-4 w-4 shrink-0 text-blue-600" />
                    <span className="text-xs font-semibold text-[#0a2d4a]">{item.label}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── Pricing connector ───────────────── */}
      <section className="border-y border-slate-100 bg-slate-50 py-16">
        <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
          <Reveal>
            <h2 className="text-2xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-3xl">
              One ERP. One predictable price.
            </h2>
            <p className="mt-3 text-base text-slate-600 sm:text-lg">
              Choose a plan based on your organization size. No per-employee pricing.
            </p>
            <Link
              href="/pricing"
              className="mt-6 inline-flex items-center gap-1.5 rounded text-sm font-semibold text-blue-600 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              View Pricing
              <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ───────────────── Final CTA ───────────────── */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0a2d4a] to-[#0d3a5f] px-8 py-14 text-center shadow-2xl sm:px-16 sm:py-16">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl"
              />
              <h2 className="relative text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                See Alea ERP in action
              </h2>
              <p className="relative mx-auto mt-4 max-w-xl text-lg text-slate-300">
                Explore how HR, payroll, finance, procurement and operations work
                together in one platform.
              </p>
              <div className="relative mt-9 flex flex-col justify-center gap-3 sm:flex-row">
                <Link
                  href="/contact"
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a2d4a]"
                >
                  Book a Demo
                  <ArrowRight
                    aria-hidden="true"
                    className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                  />
                </Link>
                <Link
                  href="/pricing"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-8 py-3.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a2d4a]"
                >
                  View Pricing
                </Link>
              </div>
              <p className="relative mt-5 text-sm text-slate-400">
                Prefer hands-on?{" "}
                <Link
                  href="/demo-access"
                  className="font-semibold text-blue-300 underline-offset-2 hover:text-blue-200 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
                >
                  Explore the live demo
                </Link>
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </MarketingLayout>
  );
}
